/**
 * Auth Guard - Include in <head> of protected pages to redirect unauthenticated users.
 *
 * Usage: Just add this script in <head> of any protected page:
 *     <script src="/js/auth-guard.js"></script>
 *
 * The script automatically hides the body until auth succeeds.
 * It also exposes window.__authPromise for common.ts to await,
 * avoiding duplicate /api/auth/me requests.
 */

interface User {
    id: number;
    username: string;
    is_admin: boolean;
}

declare global {
    interface Window {
        __authPromise?: Promise<User>;
    }
}

const API_BASE = "/api";
const LOGIN_PATH = "/pages/login.html";

// Apply saved theme immediately to prevent flash of wrong theme
const savedTheme = localStorage.getItem("theme") || "light";
document.documentElement.setAttribute("data-theme", savedTheme);

// Immediately inject style to hide body before it renders
const hideStyle = document.createElement("style");
hideStyle.textContent = "body { display: none !important; }";
document.head.appendChild(hideStyle);

// Skip auth check on login page
if (!window.location.pathname.includes(LOGIN_PATH)) {
    async function guardLogic(): Promise<User> {
        try {
            let res = await fetch(API_BASE + "/auth/me", {
                credentials: "include",
            });

            if (res.status === 401) {
                const refresh = await fetch(API_BASE + "/auth/refresh", {
                    method: "POST",
                    credentials: "include",
                });
                if (refresh.ok) {
                    res = await fetch(API_BASE + "/auth/me", {
                        credentials: "include",
                    });
                }
            }

            if (!res.ok) {
                throw new Error("Not authenticated");
            }

            hideStyle.remove();
            return (await res.json()) as User;
        } catch (err) {
            window.location.href = LOGIN_PATH;
            throw err;
        }
    }

    window.__authPromise = guardLogic();
} else {
    hideStyle.remove();
}

export {};
